import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { DevisReportsSection } from "@/components/DevisReportsSection";
import { Switch } from "@/components/ui/switch";

const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
export const eur = (n) => (Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " € TTC";
export const dfr = (s) => {
  if (!s) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return `${+m[3]} ${MOIS[+m[2] - 1]} ${m[1]}`;
  const d = new Date(s);
  return isNaN(d) ? s : d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", timeZone: "America/Martinique" });
};
export const tsfr = (s) => {
  const d = new Date(s);
  return isNaN(d) ? s : d.toLocaleString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "America/Martinique" });
};

const STATUS = {
  draft: { label: "Brouillon", cls: "bg-slate-100 text-slate-600" },
  pending: { label: "En attente", cls: "bg-amber-100 text-amber-700" },
  accepted: { label: "Accepté", cls: "bg-emerald-100 text-emerald-700" },
  refused: { label: "Refusé", cls: "bg-rose-100 text-rose-700" },
  expired: { label: "Expiré", cls: "bg-rose-50 text-rose-500 ring-1 ring-rose-200" },
};
const FILTERS = [["", "Tous"], ["draft", "Brouillons"], ["pending", "En attente"], ["accepted", "Acceptés"], ["refused", "Refusés"], ["expired", "Expirés"]];

const openBlob = async (url, filename) => {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const u = URL.createObjectURL(r.data);
    if (filename) { const a = document.createElement("a"); a.href = u; a.download = filename; a.click(); }
    else window.open(u, "_blank");
    setTimeout(() => URL.revokeObjectURL(u), 10000);
  } catch (e) { toast.error(apiError(e)); }
};

export default function Devis() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const toggleSel = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/devis", { params: { q, status } });
      setRows(data);
    } catch (e) { toast.error(apiError(e)); setRows([]); }
  }, [q, status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="devis-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Devis</h1>
          <p className="text-sm text-slate-500">Importez, publiez et suivez vos devis PDF. Un devis peut exister sans chantier.</p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setAddOpen(true)} data-testid="devis-add-btn">
          <Icons.Plus className="mr-1.5 h-4 w-4" />Ajouter un devis
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([k, lbl]) => (
            <button key={k || "all"} onClick={() => setStatus(k)} data-testid={`devis-filter-${k || "all"}`}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${status === k ? "bg-emerald-600 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>{lbl}</button>
          ))}
        </div>
        <div className="relative sm:w-72">
          <Icons.Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Référence, client, site, intitulé…"
            className="h-10 w-full rounded-lg border border-slate-200 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="devis-search" />
        </div>
      </div>

      {rows === null ? (
        <div className="flex justify-center py-16"><Icons.Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
      ) : rows.length === 0 ? (
        <Card className="p-10 text-center text-sm text-slate-400" data-testid="devis-empty">Aucun devis.</Card>
      ) : (
        <div className="space-y-3">
          {rows.map((d) => {
            const st = STATUS[d.effective_status] || STATUS.draft;
            return (
              <Card key={d.id} className={`p-4 transition-shadow hover:shadow-md ${selected.has(d.id) ? "ring-2 ring-emerald-400" : ""}`} data-testid={`devis-row-${d.id}`}>
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggleSel(d.id)} className="h-4 w-4 shrink-0 cursor-pointer accent-emerald-600" data-testid={`devis-select-${d.id}`} />
                  <div className="flex flex-1 cursor-pointer flex-wrap items-center justify-between gap-3" onClick={() => setDetail(d.id)}>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span>
                      {d.signed_pending && <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[11px] font-semibold text-white" data-testid={`devis-signed-pill-${d.id}`}>Signé ✔</span>}
                      {d.read_at ? <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${d.read_pending ? "bg-sky-600 text-white" : "bg-sky-50 text-sky-700"}`} title={`Ouvert par ${d.read_by || "le client"}`} data-testid={`devis-read-pill-${d.id}`}><Icons.Eye className="mr-1 inline h-3 w-3" />Lu le {tsfr(d.read_at)}</span>
                        : d.published && d.effective_status === "pending" && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500" data-testid={`devis-unread-pill-${d.id}`}>Non lu</span>}
                      {d.client_hold && d.effective_status === "pending" && <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-700" data-testid={`devis-hold-pill-${d.id}`}>En attente client</span>}
                      <span className="font-semibold text-slate-800">{d.title}</span>
                      <span className="text-xs text-slate-400">· {d.reference}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
                      <span className="flex items-center gap-1"><Icons.Building2 className="h-3.5 w-3.5" />{d.client_name} · {d.site_name}</span>
                      <span className="flex items-center gap-1"><Icons.Calendar className="h-3.5 w-3.5" />Émis le {dfr(d.issue_date)}</span>
                      {d.validity_date && <span className="flex items-center gap-1"><Icons.Clock className="h-3.5 w-3.5" />Valide jusqu'au {dfr(d.validity_date)}</span>}
                    </div>
                  </div>
                  <div className="text-right"><div className="font-display text-lg font-bold text-slate-900">{eur(d.amount_ttc)}</div></div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {selected.size > 0 && (
        <div className="sticky bottom-4 z-10 mx-auto flex max-w-2xl flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-white/95 p-3 shadow-lg backdrop-blur" data-testid="devis-bulk-bar">
          <span className="text-sm font-medium text-slate-700">{selected.size} devis sélectionné{selected.size > 1 ? "s" : ""}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="devis-bulk-clear">Annuler</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setBulkOpen(true)} data-testid="devis-bulk-open"><Icons.Send className="mr-1.5 h-4 w-4" />Aperçu de l'envoi groupé</Button>
          </div>
        </div>
      )}

      {addOpen && <DevisForm onClose={() => setAddOpen(false)} onSaved={(openId) => { setAddOpen(false); load(); if (openId) setDetail(openId); }} />}
      {detail && <DevisDetail id={detail} onClose={() => setDetail(null)} onChanged={load} />}
      {bulkOpen && <BulkEmailDialog rows={(rows || []).filter((r) => selected.has(r.id))} onClose={() => setBulkOpen(false)} onSent={() => { setBulkOpen(false); setSelected(new Set()); load(); }} />}
    </div>
  );
}

export function DevisForm({ onClose, onSaved, initial, lockClientSite = false, sourceFile = null }) {
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [visits, setVisits] = useState([]);
  const [f, setF] = useState({ reference: "", title: "", client_id: "", site_id: "", visit_id: "", issue_date: new Date().toISOString().slice(0, 10), validity_date: "", amount_ttc: "", internal_note: "", ...(initial || {}) });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recap, setRecap] = useState(false);
  const [inclReport, setInclReport] = useState(false);

  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!f.client_id) { setSites([]); return; }
    api.get(`/clients/${f.client_id}`).then((r) => setSites(r.data.sites || [])).catch(() => setSites([]));
  }, [f.client_id]);
  useEffect(() => {
    if (!f.site_id) { setVisits([]); return; }
    api.get(`/sites/${f.site_id}`).then((r) => setVisits(r.data.visits || [])).catch(() => setVisits([]));
  }, [f.site_id]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const [reading, setReading] = useState(false);
  const onPdf = async (fl) => {
    setFile(fl);
    if (!fl) return;
    setReading(true);
    try {
      const fd = new FormData(); fd.append("file", fl);
      const { data } = await api.post("/devis/parse-pdf", fd);
      const found = [data.reference, data.title, data.amount_ttc].filter(Boolean).length;
      setF((s) => ({ ...s, reference: data.reference || s.reference, title: data.title || s.title,
        amount_ttc: data.amount_ttc != null ? String(data.amount_ttc) : s.amount_ttc }));
      if (found === 3) toast.success("Référence, intitulé et montant remplis depuis le PDF — vérifiez-les.");
      else if (found) toast.info("Une partie des informations a été remplie depuis le PDF — complétez le reste.");
      else toast.info("Aucune information reconnue dans ce PDF — saisissez-les manuellement.");
    } catch { toast.error("Lecture du PDF impossible — saisissez les informations manuellement."); }
    finally { setReading(false); }
  };
  const valid = f.reference && f.title && f.client_id && f.site_id && f.issue_date && f.amount_ttc && (file || sourceFile);

  const save = async (publish, incl = false) => {
    if (!valid) { toast.error("Complétez la référence, l'intitulé, le client, le site, la date, le montant et le PDF."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => { if (v !== "") fd.append(k, v); });
      if (file) fd.append("file", file);
      else if (sourceFile) fd.append("source_file_id", sourceFile.id);
      const { data } = await api.post("/devis", fd);
      if (publish) {
        await api.post(`/devis/${data.id}/publish`, { include_report: incl });
        toast.success(incl ? "Devis publié (avec rapport si validé)" : "Devis publié dans l'espace client");
      } else toast.success("Devis enregistré en brouillon");
      onSaved(data.id);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const clientName = clients.find((c) => c.id === f.client_id)?.name;
  const siteName = sites.find((s) => s.id === f.site_id)?.name;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="devis-form-dialog">
        <DialogHeader><DialogTitle>Ajouter un devis</DialogTitle>
          <DialogDescription>Le PDF importé reste le document de référence (aucun recalcul de TVA).</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Référence *</Label><Input value={f.reference} onChange={set("reference")} placeholder="DEV-2026-001" data-testid="devis-f-ref" /></div>
            <div className="space-y-1.5"><Label>Montant TTC (€) *</Label><Input type="number" step="0.01" value={f.amount_ttc} onChange={set("amount_ttc")} placeholder="1600.00" data-testid="devis-f-amount" /></div>
          </div>
          <div className="space-y-1.5"><Label>Intitulé *</Label><Input value={f.title} onChange={set("title")} placeholder="Nettoyage des gouttières" data-testid="devis-f-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Client *</Label>
              <select value={f.client_id} onChange={(e) => setF((s) => ({ ...s, client_id: e.target.value, site_id: "", visit_id: "" }))} disabled={lockClientSite} className="h-10 w-full rounded-md border border-slate-200 px-2 text-sm disabled:bg-slate-50" data-testid="devis-f-client">
                <option value="">— Choisir —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5"><Label>Site *</Label>
              <select value={f.site_id} onChange={(e) => setF((s) => ({ ...s, site_id: e.target.value, visit_id: "" }))} disabled={!f.client_id || lockClientSite} className="h-10 w-full rounded-md border border-slate-200 px-2 text-sm disabled:bg-slate-50" data-testid="devis-f-site">
                <option value="">— Choisir —</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Visite associée (facultatif)</Label>
            <select value={f.visit_id} onChange={set("visit_id")} disabled={!f.site_id} className="h-10 w-full rounded-md border border-slate-200 px-2 text-sm disabled:bg-slate-50" data-testid="devis-f-visit">
              <option value="">— Aucune —</option>
              {visits.map((v) => <option key={v.id} value={v.id}>{v.reference} · {dfr(v.date)}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Date d'émission *</Label><Input type="date" value={f.issue_date} onChange={set("issue_date")} data-testid="devis-f-issue" /></div>
            <div className="space-y-1.5"><Label>Date de validité</Label><Input type="date" value={f.validity_date} onChange={set("validity_date")} data-testid="devis-f-validity" /></div>
          </div>
          <div className="space-y-1.5"><Label>Fichier PDF {sourceFile ? "" : "*"}</Label>
            {sourceFile && !file && (
              <div className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-testid="devis-f-sourcefile">
                <Icons.FileCheck className="h-4 w-4 shrink-0" /><span className="truncate">Document existant : {sourceFile.name}</span>
              </div>
            )}
            <input type="file" accept="application/pdf" onChange={(e) => onPdf(e.target.files?.[0] || null)} className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-emerald-50 file:px-3 file:py-2 file:text-emerald-700" data-testid="devis-f-file" />
            {reading && <p className="flex items-center gap-1.5 text-xs text-emerald-700" data-testid="devis-f-reading"><Icons.Loader2 className="h-3.5 w-3.5 animate-spin" />Lecture du PDF en cours…</p>}
            <p className="text-xs text-slate-400">La référence, l'intitulé et le montant TTC sont remplis automatiquement depuis le PDF.</p>
            {sourceFile && <p className="text-xs text-slate-400">Laissez vide pour réutiliser le document existant, ou importez un nouveau PDF.</p>}
          </div>
          <div className="space-y-1.5"><Label>Note interne (jamais visible par le client)</Label><Textarea value={f.internal_note} onChange={set("internal_note")} rows={2} data-testid="devis-f-note" /></div>
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={() => save(false)} disabled={busy} data-testid="devis-save-draft">Enregistrer en brouillon</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { if (!valid) { toast.error("Complétez les champs requis et le PDF."); return; } setRecap(true); }} disabled={busy} data-testid="devis-publish-btn">Publier dans l'espace client</Button>
        </DialogFooter>

        <Dialog open={recap} onOpenChange={setRecap}>
          <DialogContent data-testid="devis-recap-dialog">
            <DialogHeader><DialogTitle>Confirmer la publication</DialogTitle>
              <DialogDescription>Vérifiez le destinataire avant publication.</DialogDescription></DialogHeader>
            <div className="space-y-1.5 rounded-lg bg-slate-50 p-4 text-sm">
              <div><span className="text-slate-500">Client :</span> <strong>{clientName}</strong></div>
              <div><span className="text-slate-500">Site :</span> <strong>{siteName}</strong></div>
              <div><span className="text-slate-500">Montant :</span> <strong>{eur(f.amount_ttc)}</strong></div>
              <div><span className="text-slate-500">Document :</span> <strong>{file?.name || (sourceFile && sourceFile.name)}</strong></div>
            </div>
            {f.visit_id && (
              <label className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="devis-recap-report">
                <input type="checkbox" checked={inclReport} onChange={(e) => setInclReport(e.target.checked)} className="mt-0.5 h-4 w-4 accent-emerald-600" data-testid="devis-recap-report-cb" />
                <span>Joindre le rapport de visite associé — le client le retrouvera dans ses Documents (uniquement si la visite est validée).</span>
              </label>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setRecap(false)}>Annuler</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { setRecap(false); save(true, inclReport); }} disabled={busy} data-testid="devis-recap-confirm">Confirmer et publier</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

function DevisDetail({ id, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [accDate, setAccDate] = useState(new Date().toISOString().slice(0, 10));
  const [accFile, setAccFile] = useState(null);
  const [accNote, setAccNote] = useState("");
  const [refuseOpen, setRefuseOpen] = useState(false);
  const [refuseReason, setRefuseReason] = useState("");
  const [attachOpen, setAttachOpen] = useState(false);
  const [avail, setAvail] = useState([]);
  const [attachId, setAttachId] = useState("");
  const [busy, setBusy] = useState(false);
  const [includeReport, setIncludeReport] = useState(false);
  const [delOpen, setDelOpen] = useState(false);

  const load = useCallback(() => { api.get(`/devis/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d?.read_pending) api.post(`/devis/${id}/ack-read`).then(() => { window.dispatchEvent(new Event("devis-signed-changed")); onChanged?.(); }).catch(() => {});
  }, [d?.read_pending, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn) => { setBusy(true); try { await fn(); load(); onChanged?.(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const publish = () => act(async () => { const { data } = await api.post(`/devis/${id}/publish`, { include_report: includeReport }); toast.success(data.emailed?.length ? `Publié et notifié (${data.emailed.length} e-mail)` : "Publié dans l'espace client"); });
  const refuse = () => act(async () => { await api.post(`/devis/${id}/refuse`, { reason: refuseReason }); setRefuseOpen(false); toast.success("Devis marqué refusé"); });
  const accept = () => act(async () => {
    const fd = new FormData(); fd.append("accepted_date", accDate); if (accNote) fd.append("note", accNote); if (accFile) fd.append("file", accFile);
    await api.post(`/devis/${id}/accept`, fd); setAcceptOpen(false); toast.success("Accord enregistré");
  });

  const st = d && (STATUS[d.effective_status] || STATUS.draft);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="devis-detail-dialog">
        {!d ? <div className="flex justify-center py-10"><Icons.Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div> : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2"><span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span>
                {d.worksite && <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">Chantier lié · {d.worksite.reference}</span>}</div>
              <DialogTitle className="mt-1">{d.title}</DialogTitle>
              <DialogDescription>{d.reference} · {d.client_name} · {d.site_name}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400">Montant</div><div className="font-display text-lg font-bold text-slate-900">{eur(d.amount_ttc)}</div></div>
                <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400">Émis le</div><div className="font-medium">{dfr(d.issue_date)}</div>{d.validity_date && <div className="text-xs text-slate-500">Valide jusqu'au {dfr(d.validity_date)}</div>}</div>
              </div>
              {d.visit_reference && <div className="text-slate-600">Visite associée : <strong>{d.visit_reference}</strong></div>}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => openBlob(`/devis/${id}/pdf`)} data-testid="devis-view-pdf"><Icons.Eye className="mr-1.5 h-4 w-4" />Prévisualiser le PDF</Button>
                <Button size="sm" variant="outline" onClick={() => openBlob(`/devis/${id}/pdf`, `${d.reference}.pdf`)} data-testid="devis-dl-pdf"><Icons.Download className="mr-1.5 h-4 w-4" />Télécharger</Button>
                {d.acceptance_file && <Button size="sm" variant="outline" onClick={() => openBlob(`/devis/${id}/acceptance-file`, "justificatif")} data-testid="devis-dl-justif"><Icons.FileCheck className="mr-1.5 h-4 w-4" />Justificatif d'accord</Button>}
              </div>
              <div className={`flex items-center gap-2 rounded-lg p-3 text-sm ${d.read_at ? "bg-sky-50 text-sky-800" : "bg-slate-50 text-slate-500"}`} data-testid="devis-read-status">
                <Icons.Eye className="h-4 w-4" />
                {d.read_at ? `Lu par ${d.read_by || "le client"} le ${tsfr(d.read_at)}${d.read_count > 1 ? ` · ouvert ${d.read_count} fois, dernière fois le ${tsfr(d.last_read_at)}` : ""}` : d.published ? "Pas encore ouvert par le client" : "Non publié dans l'espace client"}
                {d.reminder_sent_at && <span className="ml-1 text-xs" data-testid="devis-reminder-sent">· Relance envoyée le {tsfr(d.reminder_sent_at)}</span>}
                {!d.read_at && !d.reminder_sent_at && d.published && d.effective_status === "pending" && d.reminder_status !== "no_email" && (
                  <label className="ml-auto flex items-center gap-2 text-xs text-slate-600">
                    <Switch checked={!d.reminder_disabled} onCheckedChange={async (v) => {
                      try { await api.post(`/devis/${d.id}/reminder`, { enabled: v }); toast.success(v ? "Relance automatique réactivée" : "Relance automatique désactivée pour ce devis"); load(); }
                      catch (e) { toast.error(apiError(e)); }
                    }} data-testid="devis-reminder-toggle" />
                    {d.reminder_disabled ? "Relance automatique désactivée" : "Relance automatique 3 jours après publication"}
                  </label>)}
                {d.reminder_status === "no_email" && <span className="ml-1 text-xs text-amber-700">· relance impossible : aucune adresse e-mail client</span>}
              </div>
              {d.signed_pending && (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-300 bg-emerald-50 p-3" data-testid="devis-signed-banner">
                  <span className="flex items-center gap-2 text-sm font-medium text-emerald-800"><Icons.PenLine className="h-4 w-4" />Signé électroniquement par {d.accepted_by_name || "le client"}{d.signature?.at ? ` le ${tsfr(d.signature.at)}` : ""}</span>
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => act(async () => { await api.post(`/devis/${id}/ack-signature`); window.dispatchEvent(new Event("devis-signed-changed")); toast.success("Signature marquée comme traitée"); })} data-testid="devis-ack-signature"><Icons.Check className="mr-1.5 h-4 w-4" />Marquer comme traité</Button>
                </div>
              )}
              {d.internal_note && <div className="rounded-lg border border-amber-200 bg-amber-50 p-3"><div className="text-xs font-medium text-amber-700">Note interne (non visible par le client)</div><div className="text-slate-700">{d.internal_note}</div></div>}
              {d.accepted_date && <div className="rounded-lg bg-emerald-50 p-3 text-emerald-800">Accord enregistré le <strong>{dfr(d.accepted_date)}</strong>{d.accepted_by_name ? ` par ${d.accepted_by_name}` : ""}.</div>}
              {d.report_available && (d.status === "draft" || d.published) && (
                <label className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="devis-include-report">
                  <input type="checkbox" checked={includeReport} onChange={(e) => setIncludeReport(e.target.checked)} className="mt-0.5 h-4 w-4 accent-emerald-600" data-testid="devis-include-report-cb" />
                  <span>Joindre le rapport de visite{d.visit_reference ? ` (${d.visit_reference})` : ""} lors de l'envoi — le client le retrouvera dans ses Documents.{d.report_published ? " (déjà visible par le client)" : ""}</span>
                </label>
              )}

              <DevisReportsSection d={d} onChanged={load} />

              <div>
                <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Historique</div>
                <ol className="space-y-1.5 border-l-2 border-slate-100 pl-3">
                  {(d.history || []).slice().reverse().map((h, i) => (
                    <li key={i} className="text-xs text-slate-600"><span className="font-medium text-slate-700">{tsfr(h.at)}</span> — {h.detail}{h.by_name ? ` (${h.by_name})` : ""}</li>
                  ))}
                </ol>
              </div>
            </div>
            <DialogFooter className="flex-wrap gap-2">
              {d.status === "draft" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={publish} disabled={busy} data-testid="devis-detail-publish">Publier</Button>}
              {["pending", "expired"].includes(d.effective_status) && <>
                <Button size="sm" variant="outline" className="text-rose-600" onClick={() => setRefuseOpen(true)} disabled={busy} data-testid="devis-detail-refuse">Refuser</Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setAcceptOpen(true)} disabled={busy} data-testid="devis-detail-accept">Enregistrer l'accord</Button>
              </>}
              {d.status === "accepted" && !d.worksite && <>
                <Button size="sm" variant="outline" onClick={async () => { try { const { data } = await api.get(`/devis/worksites/available`, { params: { client_id: d.client_id, site_id: d.site_id } }); setAvail(data); setAttachId(""); setAttachOpen(true); } catch (e) { toast.error(apiError(e)); } }} data-testid="devis-attach-open">Rattacher à un chantier</Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act(async () => { const { data } = await api.post(`/devis/${id}/create-worksite`, { title: d.title }); toast.success("Chantier créé : " + data.worksite.reference); })} data-testid="devis-create-ws">Créer le chantier</Button>
              </>}
              {d.status !== "accepted" && <Button size="sm" variant="outline" disabled={busy} onClick={() => act(async () => { const { data } = await api.post(`/devis/${id}/notify`, { include_report: includeReport }); toast.success(data.emailed?.length ? `Notification renvoyée (${data.emailed.length} e-mail)` : "Aucune adresse e-mail client enregistrée"); })} data-testid="devis-resend"><Icons.Send className="mr-1.5 h-4 w-4" />Renvoyer la notification</Button>}
              <Button size="sm" variant="outline" className="text-rose-600" disabled={busy} onClick={() => setDelOpen(true)} data-testid="devis-detail-delete"><Icons.Trash2 className="mr-1.5 h-4 w-4" />Supprimer</Button>
              {d.worksite && <span className="text-xs font-medium text-emerald-700">Chantier lié : {d.worksite.reference}</span>}
            </DialogFooter>

            <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}>
              <DialogContent data-testid="devis-accept-dialog">
                <DialogHeader><DialogTitle>Enregistrer l'accord du client</DialogTitle>
                  <DialogDescription>La consultation ou une question ne valent jamais acceptation. Confirmez après réception de l'accord.</DialogDescription></DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-1.5"><Label>Date de l'accord *</Label><Input type="date" value={accDate} onChange={(e) => setAccDate(e.target.value)} data-testid="devis-acc-date" /></div>
                  <div className="space-y-1.5"><Label>Devis signé / justificatif (facultatif)</Label><input type="file" onChange={(e) => setAccFile(e.target.files?.[0] || null)} className="block w-full text-sm" data-testid="devis-acc-file" /></div>
                  <div className="space-y-1.5"><Label>Note (facultatif)</Label><Input value={accNote} onChange={(e) => setAccNote(e.target.value)} data-testid="devis-acc-note" /></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setAcceptOpen(false)}>Annuler</Button>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={accept} disabled={busy || !accDate} data-testid="devis-acc-confirm">Confirmer l'accord</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={refuseOpen} onOpenChange={setRefuseOpen}>
              <DialogContent data-testid="devis-refuse-dialog">
                <DialogHeader><DialogTitle>Marquer le devis refusé</DialogTitle></DialogHeader>                <div className="space-y-1.5"><Label>Motif (facultatif)</Label><Textarea value={refuseReason} onChange={(e) => setRefuseReason(e.target.value)} rows={2} data-testid="devis-refuse-reason" /></div>
                <DialogFooter><Button variant="outline" onClick={() => setRefuseOpen(false)}>Annuler</Button>
                  <Button className="bg-rose-600 hover:bg-rose-700" onClick={refuse} disabled={busy} data-testid="devis-refuse-confirm">Confirmer le refus</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={attachOpen} onOpenChange={setAttachOpen}>
              <DialogContent data-testid="devis-attach-dialog">
                <DialogHeader><DialogTitle>Rattacher à un chantier existant</DialogTitle>
                  <DialogDescription>Uniquement les chantiers du même client et du même site. L'avancement du chantier n'est pas modifié.</DialogDescription></DialogHeader>
                {avail.length === 0 ? <p className="text-sm text-slate-500">Aucun chantier disponible pour ce site.</p>
                  : <select value={attachId} onChange={(e) => setAttachId(e.target.value)} className="h-10 w-full rounded-md border border-slate-200 px-2 text-sm" data-testid="devis-attach-select">
                      <option value="">— Choisir —</option>
                      {avail.map((w) => <option key={w.id} value={w.id}>{w.reference} · {w.title}</option>)}
                    </select>}
                <DialogFooter><Button variant="outline" onClick={() => setAttachOpen(false)}>Annuler</Button>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={!attachId || busy} onClick={() => act(async () => { await api.post(`/devis/${id}/attach-worksite`, { worksite_id: attachId }); setAttachOpen(false); toast.success("Devis rattaché au chantier"); })} data-testid="devis-attach-confirm">Rattacher</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={delOpen} onOpenChange={setDelOpen}>
              <DialogContent data-testid="devis-delete-dialog">
                <DialogHeader><DialogTitle>Supprimer le devis</DialogTitle>
                  <DialogDescription>Le devis sera retiré de la liste et de l'espace client (y compris s'il a été signé). Si une intervention a déjà été créée, elle n'est pas supprimée. Cette action est irréversible.</DialogDescription></DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDelOpen(false)}>Annuler</Button>
                  <Button className="bg-rose-600 hover:bg-rose-700" disabled={busy}
                    onClick={async () => { setBusy(true); try { await api.delete(`/devis/${id}`); toast.success("Devis supprimé"); onChanged?.(); onClose(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } }}
                    data-testid="devis-delete-confirm">Supprimer définitivement</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


function BulkEmailDialog({ rows, onClose, onSent }) {
  const [busy, setBusy] = useState(false);
  const eligible = rows.filter((d) => d.published && d.effective_status === "pending");
  const excluded = rows.filter((d) => !(d.published && d.effective_status === "pending"));
  const clients = [...new Set(eligible.map((d) => d.client_id))];
  const sites = [...new Set(eligible.map((d) => d.site_id))];
  const sameTarget = clients.length <= 1 && sites.length <= 1;
  const canSend = eligible.length > 0 && sameTarget;

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/devis/notify-group", { devis_ids: eligible.map((d) => d.id) });
      toast.success(data.emailed?.length ? `E-mail groupé envoyé à ${data.emailed.length} destinataire(s)` : "Aucune adresse e-mail client enregistrée");
      onSent();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="devis-bulk-dialog">
        <DialogHeader><DialogTitle>Envoi groupé de devis</DialogTitle>
          <DialogDescription>Un seul e-mail récapitulatif est envoyé au client (devis d'un même client et d'un même site).</DialogDescription></DialogHeader>
        {!sameTarget && <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="devis-bulk-error">Sélectionnez uniquement des devis d'un même client et d'un même site.</div>}
        {eligible.length > 0 ? (
          <div className="space-y-2">
            <p className="text-sm text-slate-600"><strong>{eligible[0].client_name}</strong> · {eligible[0].site_name}</p>
            <div className="rounded-lg bg-slate-50 p-3 text-sm">
              <p className="mb-1 font-medium text-slate-700">Devis inclus ({eligible.length}) :</p>
              <ul className="list-inside list-disc text-slate-600">{eligible.map((d) => <li key={d.id}>{d.reference} — {d.title} · {eur(d.amount_ttc)}</li>)}</ul>
            </div>
            <p className="text-xs text-slate-400">Les montants ne sont pas additionnés : le client choisit les propositions à retenir.</p>
          </div>
        ) : <p className="text-sm text-slate-500" data-testid="devis-bulk-none">Aucun devis éligible : un devis doit être publié et en attente de réponse.</p>}
        {excluded.length > 0 && <p className="text-xs text-slate-400" data-testid="devis-bulk-excluded">{excluded.length} devis exclu(s) de l'envoi (brouillon, accepté, refusé ou expiré).</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={!canSend || busy} onClick={send} data-testid="devis-bulk-send">
            {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : "Confirmer l'envoi groupé"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
